// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	colorNumberToCss,
	SERVER_TAG_DEFAULT_BADGE_ID,
	SERVER_TAG_DEFAULT_COLOR,
	ServerTagBadgeGlyph,
	type ServerTagBadgeId,
} from '@app/features/guild/components/server_tag/ServerTagBadgeIcons';
import {ServerTagGuildProfileCard} from '@app/features/guild/components/server_tag/ServerTagGuildProfileCard';
import styles from '@app/features/guild/components/server_tag/ServerTagBadge.module.css';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {isServerTagBadgeId} from '@fluxer/constants/src/ServerTagConstants';
import type {UserPrimaryGuildResponse} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {clsx} from 'clsx';
import type React from 'react';
import {useCallback} from 'react';

export interface ServerTagBadgeProps {
	primaryGuild: UserPrimaryGuildResponse;
	size?: 'sm' | 'md';
	className?: string;
	interactive?: boolean;
}

function resolveBadgeId(badgeId: string): ServerTagBadgeId {
	return isServerTagBadgeId(badgeId) ? badgeId : SERVER_TAG_DEFAULT_BADGE_ID;
}

export function getUserPrimaryGuildTag(
	user: {primaryGuild?: UserPrimaryGuildResponse | null | undefined} | null | undefined,
): UserPrimaryGuildResponse | null {
	const tag = user?.primaryGuild;
	if (!tag || !tag.tag || !tag.identity_guild_id) {
		return null;
	}
	return tag;
}

export const ServerTagBadge: React.FC<ServerTagBadgeProps> = ({
	primaryGuild,
	size = 'sm',
	className,
	interactive = true,
}) => {
	const accent = colorNumberToCss(primaryGuild.color ?? SERVER_TAG_DEFAULT_COLOR);
	const badgeId = resolveBadgeId(primaryGuild.badge_id);
	const glyphSize = size === 'md' ? 14 : 12;
	const content = (
		<span
			className={clsx(styles.badge, size === 'md' ? styles.badgeMd : styles.badgeSm, className)}
			data-flx="guild.server-tag-badge.root"
		>
			<ServerTagBadgeGlyph badgeId={badgeId} size={glyphSize} accent={accent} />
			<span className={styles.tagText}>{primaryGuild.tag}</span>
		</span>
	);

	const renderCard = useCallback(
		({popoutKey, onClose}: {popoutKey: string | number; onClose: () => void}) => (
			<ServerTagGuildProfileCard primaryGuild={primaryGuild} popoutKey={popoutKey} onClose={onClose} />
		),
		[primaryGuild],
	);

	const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
		// Popout clones the child and handles activation; keep bubbling stopped for nested rows.
		if (!isKeyboardActivationKey(event.key)) return;
		event.stopPropagation();
	}, []);

	if (!interactive) {
		return content;
	}

	return (
		<Popout
			position="right"
			animationType="profile-slide"
			constrainHeight={false}
			freezePosition
			keepOpenOnTargetUnmount
			tooltip={primaryGuild.name}
			tooltipPosition="top"
			containerClass={styles.popoutNoShadow}
			render={renderCard}
			shouldOpenOnClick={() => true}
			data-flx="guild.server-tag-badge.popout"
		>
			<FocusRing offset={-2} data-flx="guild.server-tag-badge.focus-ring">
				<span
					className={styles.hitTarget}
					aria-label={primaryGuild.name}
					role="button"
					tabIndex={0}
					onKeyDown={handleKeyDown}
					data-flx="guild.server-tag-badge.button"
				>
					{content}
				</span>
			</FocusRing>
		</Popout>
	);
};

export const UserServerTagBadge: React.FC<{
	user: {primaryGuild?: UserPrimaryGuildResponse | null | undefined} | null | undefined;
	size?: 'sm' | 'md';
	className?: string;
	interactive?: boolean;
}> = ({user, size, className, interactive}) => {
	const tag = getUserPrimaryGuildTag(user);
	if (!tag) return null;
	return <ServerTagBadge primaryGuild={tag} size={size} className={className} interactive={interactive} />;
};
